import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  useI18n,
  watch,
} from '#imports';
import type { NewsSearchItem } from '../../types';
import type { NewsSearchEmits, NewsSearchProps } from './types';

const EMPTY_SEARCH_DELAY_MS = 2000;
const MOBILE_SEARCH_BREAKPOINT = 576;

const cloneItems = (items: NewsSearchItem[]): NewsSearchItem[] =>
  items.map((item) => ({ ...item }));

export const useNewsSearchModel = (
  props: NewsSearchProps,
  emit: NewsSearchEmits,
) => {
  const { t } = useI18n({ useScope: 'local' });
  const form = ref<HTMLFormElement | null>(null);
  const input = ref<HTMLInputElement | null>(null);
  const inputValue = ref(props.search.value);
  const previousInputValue = ref(props.search.value);
  const items = ref<NewsSearchItem[]>(cloneItems(props.search.items));
  const isSticky = ref(false);
  const isDropdownActive = ref(true);
  let emptySearchTimeoutId: ReturnType<typeof setTimeout> | null = null;
  let observer: IntersectionObserver | null = null;

  const selectedFilters = computed(() =>
    items.value.filter((item) => item.selected).map((item) => item.alias),
  );
  const comboboxPlaceholder = computed(() =>
    selectedFilters.value.length
      ? selectedFilters.value.join(', ')
      : t('category_select'),
  );

  const emitUpdate = () => {
    emit('update', {
      value: inputValue.value,
      group: selectedFilters.value,
    });
  };

  const submit = () => {
    emitUpdate();
    emit('reload');
    previousInputValue.value = inputValue.value;
  };

  const handleInputKeyup = () => {
    if (inputValue.value) {
      if (emptySearchTimeoutId) clearTimeout(emptySearchTimeoutId);
      return;
    }

    if (emptySearchTimeoutId) clearTimeout(emptySearchTimeoutId);
    emptySearchTimeoutId = setTimeout(() => {
      if (!inputValue.value && previousInputValue.value) submit();
    }, EMPTY_SEARCH_DELAY_MS);
  };

  const handleSearchButtonClick = (event: MouseEvent) => {
    if (window.innerWidth >= MOBILE_SEARCH_BREAKPOINT) return;
    if (isDropdownActive.value) {
      event.preventDefault();
      isDropdownActive.value = false;
    }
    input.value?.focus();
  };

  const handleSelectionChange = () => {
    emitUpdate();
    emit('reload');
  };

  const toggleAllItems = (selected: boolean) => {
    for (const item of items.value) item.selected = selected;
    handleSelectionChange();
  };

  const activateDropdown = () => {
    isDropdownActive.value = true;
  };

  const clearInput = () => {
    if (emptySearchTimeoutId) {
      clearTimeout(emptySearchTimeoutId);
      emptySearchTimeoutId = null;
    }

    inputValue.value = '';
    previousInputValue.value = '';
    emitUpdate();
    emit('reload');
    input.value?.focus();
  };

  watch(
    () => props.search.items,
    (nextItems) => {
      items.value = cloneItems(nextItems);
    },
  );

  watch(
    () => props.search.value,
    (value) => {
      inputValue.value = value;
      previousInputValue.value = value;
    },
  );

  onMounted(() => {
    if (!form.value) return;
    observer = new IntersectionObserver(
      ([entry]) => {
        isSticky.value = entry ? entry.intersectionRatio < 1 : false;
      },
      { threshold: [1] },
    );
    observer.observe(form.value);
  });

  onBeforeUnmount(() => {
    if (emptySearchTimeoutId) clearTimeout(emptySearchTimeoutId);
    observer?.disconnect();
  });

  return {
    t,
    form,
    input,
    inputValue,
    items,
    isSticky,
    isDropdownActive,
    comboboxPlaceholder,
    submit,
    handleInputKeyup,
    handleSearchButtonClick,
    handleSelectionChange,
    toggleAllItems,
    activateDropdown,
    clearInput,
  };
};
